import React, { useState } from 'react';
import { Palette, Copy, Check, Pipette } from 'lucide-react';

interface RGB { r: number; g: number; b: number; }
interface HSL { h: number; s: number; l: number; }

const ColorConverter: React.FC = () => {
  const [hex, setHex] = useState('#3B82F6');
  const [rgb, setRgb] = useState<RGB>({ r: 59, g: 130, b: 246 });
  const [hsl, setHsl] = useState<HSL>({ h: 217, s: 91, l: 60 });
  const [copied, setCopied] = useState<string | null>(null);

  const hexToRgb = (hex: string): RGB | null => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  const rgbToHex = (r: number, g: number, b: number): string => {
    return '#' + [r, g, b].map(x => {
      const hex = Math.max(0, Math.min(255, Math.round(x))).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    }).join('').toUpperCase();
  };

  const rgbToHsl = (r: number, g: number, b: number): HSL => {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = 0, s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        case b: h = ((r - g) / d + 4) / 6; break;
      }
    }
    return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
  };

  const hslToRgb = (h: number, s: number, l: number): RGB => {
    h /= 360; s /= 100; l /= 100;
    let r, g, b;

    if (s === 0) {
      r = g = b = l;
    } else {
      const hue2rgb = (p: number, q: number, t: number) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1/6) return p + (q - p) * 6 * t;
        if (t < 1/2) return q;
        if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
        return p;
      };
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      r = hue2rgb(p, q, h + 1/3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1/3);
    }
    return { r: Math.round(r * 255), g: Math.round(g * 255), b: Math.round(b * 255) };
  };

  const handleHexChange = (value: string) => {
    let cleanHex = value.replace(/[^a-fA-F0-9#]/g, '');
    if (!cleanHex.startsWith('#')) cleanHex = '#' + cleanHex;
    if (cleanHex.length <= 7) setHex(cleanHex);
    
    if (cleanHex.length === 7) {
      const newRgb = hexToRgb(cleanHex);
      if (newRgb) {
        setRgb(newRgb);
        setHsl(rgbToHsl(newRgb.r, newRgb.g, newRgb.b));
      }
    }
  };

  const handleRgbChange = (component: keyof RGB, value: number) => {
    const newRgb = { ...rgb, [component]: Math.max(0, Math.min(255, value)) };
    setRgb(newRgb);
    setHex(rgbToHex(newRgb.r, newRgb.g, newRgb.b));
    setHsl(rgbToHsl(newRgb.r, newRgb.g, newRgb.b));
  };

  const handleHslChange = (component: keyof HSL, value: number) => {
    const max = component === 'h' ? 360 : 100;
    const newHsl = { ...hsl, [component]: Math.max(0, Math.min(max, value)) };
    setHsl(newHsl);
    const newRgb = hslToRgb(newHsl.h, newHsl.s, newHsl.l);
    setRgb(newRgb);
    setHex(rgbToHex(newRgb.r, newRgb.g, newRgb.b));
  };

  const copyToClipboard = async (text: string, type: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  const presetColors = [
    '#EF4444', '#F97316', '#EAB308', '#22C55E', '#14B8A6',
    '#3B82F6', '#8B5CF6', '#EC4899', '#000000', '#FFFFFF',
  ];

  const getContrastColor = (hex: string): string => {
    const rgb = hexToRgb(hex);
    if (!rgb) return '#000000';
    const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Color Converter</h2>
        <p className="text-gray-600">Convert between HEX, RGB, and HSL color formats</p>
      </div>

      <div className="max-w-3xl mx-auto space-y-6">
        {/* Color Preview */}
        <div
          className="h-32 rounded-xl shadow-inner flex items-center justify-center transition-colors"
          style={{ backgroundColor: hex }}
        >
          <span
            className="text-2xl font-mono font-bold"
            style={{ color: getContrastColor(hex) }}
          >
            {hex}
          </span>
        </div>

        {/* Preset Colors */}
        <div className="flex justify-center gap-2">
          {presetColors.map((color) => (
            <button
              key={color}
              onClick={() => handleHexChange(color)}
              className="w-8 h-8 rounded-full border-2 border-white shadow-md hover:scale-110 transition-transform"
              style={{ backgroundColor: color }}
              title={color}
            />
          ))}
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {/* HEX */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">HEX</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={hex}
                onChange={(e) => handleHexChange(e.target.value)}
                className="flex-1 px-3 py-2 font-mono text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500"
                maxLength={7}
              />
              <button
                onClick={() => copyToClipboard(hex, 'hex')}
                className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                {copied === 'hex' ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5 text-gray-600" />}
              </button>
            </div>
            <input
              type="color"
              value={hex}
              onChange={(e) => handleHexChange(e.target.value)}
              className="w-full h-10 mt-2 cursor-pointer rounded"
            />
          </div>

          {/* RGB */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-medium text-gray-700">RGB</label>
              <button
                onClick={() => copyToClipboard(`rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`, 'rgb')}
                className="p-1 text-gray-500 hover:text-gray-700"
              >
                {copied === 'rgb' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            {(['r', 'g', 'b'] as const).map((c) => (
              <div key={c} className="flex items-center gap-2 mb-2">
                <span className="w-6 text-sm font-medium text-gray-500 uppercase">{c}</span>
                <input
                  type="range"
                  min="0"
                  max="255"
                  value={rgb[c]}
                  onChange={(e) => handleRgbChange(c, parseInt(e.target.value))}
                  className={`flex-1 h-2 rounded-lg appearance-none cursor-pointer ${
                    c === 'r' ? 'accent-red-500' : c === 'g' ? 'accent-green-500' : 'accent-blue-500'
                  }`}
                />
                <input
                  type="number"
                  min="0"
                  max="255"
                  value={rgb[c]}
                  onChange={(e) => handleRgbChange(c, parseInt(e.target.value) || 0)}
                  className="w-16 px-2 py-1 text-sm font-mono border border-gray-300 rounded"
                />
              </div>
            ))}
            <p className="text-xs text-gray-500 font-mono mt-2">rgb({rgb.r}, {rgb.g}, {rgb.b})</p>
          </div>

          {/* HSL */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-medium text-gray-700">HSL</label>
              <button
                onClick={() => copyToClipboard(`hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)`, 'hsl')}
                className="p-1 text-gray-500 hover:text-gray-700"
              >
                {copied === 'hsl' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            {[
              { key: 'h' as const, label: 'H', max: 360, unit: '°' },
              { key: 's' as const, label: 'S', max: 100, unit: '%' },
              { key: 'l' as const, label: 'L', max: 100, unit: '%' },
            ].map(({ key, label, max, unit }) => (
              <div key={key} className="flex items-center gap-2 mb-2">
                <span className="w-6 text-sm font-medium text-gray-500">{label}</span>
                <input
                  type="range"
                  min="0"
                  max={max}
                  value={hsl[key]}
                  onChange={(e) => handleHslChange(key, parseInt(e.target.value))}
                  className="flex-1 h-2 rounded-lg appearance-none cursor-pointer accent-violet-500"
                />
                <input
                  type="number"
                  min="0"
                  max={max}
                  value={hsl[key]}
                  onChange={(e) => handleHslChange(key, parseInt(e.target.value) || 0)}
                  className="w-16 px-2 py-1 text-sm font-mono border border-gray-300 rounded"
                />
              </div>
            ))}
            <p className="text-xs text-gray-500 font-mono mt-2">hsl({hsl.h}, {hsl.s}%, {hsl.l}%)</p>
          </div>
        </div>

        {/* CSS Output */}
        <div className="bg-gray-900 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <Palette className="w-5 h-5 text-violet-400" />
            <span className="text-sm text-gray-400">CSS Values</span>
          </div>
          <div className="grid grid-cols-3 gap-4 font-mono text-sm">
            <div>
              <span className="text-gray-500">HEX:</span>
              <p className="text-white">{hex}</p>
            </div>
            <div>
              <span className="text-gray-500">RGB:</span>
              <p className="text-white">rgb({rgb.r}, {rgb.g}, {rgb.b})</p>
            </div>
            <div>
              <span className="text-gray-500">HSL:</span>
              <p className="text-white">hsl({hsl.h}, {hsl.s}%, {hsl.l}%)</p>
            </div>
          </div>
        </div>

        {/* Info */}
        <div className="bg-violet-50 rounded-xl p-4 border border-violet-100">
          <div className="flex items-start gap-3">
            <Pipette className="w-5 h-5 text-violet-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-violet-900 mb-2">Color Formats</h4>
              <p className="text-sm text-violet-700">
                HEX uses 6 hexadecimal digits (#RRGGBB). RGB uses red, green, blue values (0-255).
                HSL uses hue (0-360°), saturation (0-100%), and lightness (0-100%).
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ColorConverter;
